import { Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../../global/theme/tokens';
import useFetch from '../../../../api/useFetch';
import { ExoSkeleton } from '../../ExoSkeleton';
import ProjectTaskPhase from '../task-view/ProjectTaskPhase';
import { visiblePhases } from '../task-view/taskFilterQuery';
import TaskCard from '../task-card/TaskCard';
import TaskCardList from '../task-card/TaskCardList';
import CapacityProjectCard from './CapacityProjectCard';
import { projectCapacityStats, projectTeamMembers } from './capacityStats';
import { renderHours } from '../../../../scenes/time-tracking/worktime';

/**
 * One project of a capacity list. Collapsed it is a single row, expanded it
 * shows the phases of the project exactly like the task view of the project
 * itself does. Used by the employee capacity view and by the pool.
 */
const CapacityProject = ({
  project,
  phases,
  isOpen,
  onToggle,
  onPhasesLoaded,
  taskFilter,
  onLoadingChange,
  onStatsChange,
  extraStats = [],
  // the pool counts tasks and points, hours say nothing about a single task
  showHours = true,
  // off for a page that passes its own counters through extraStats
  showProjectStats = true,
  allowCreate = true,
  // tasks the project brought with it, listed while the card is closed
  previewTasks,
  processingTaskIds = [],
  onTaskToggle,
  onTaskEdit,
  onTaskDelete,
  onTaskUsersChange,
  onTaskFilesChange,
  onTaskLinkAdd,
  onTaskLinkDelete,
  onTaskCommentSubmit,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // the list response has no team and no edit flag, so the full project is
  // loaded once on the first expand
  const [detail, setDetail] = useState(null);
  const { apiData: detailData } = useFetch(
    isOpen && !detail ? `projects/${project.id}` : null,
  );

  useEffect(() => {
    if (detailData?.data) setDetail(detailData.data);
  }, [detailData]);

  // null path = no request: only fetched while open and not cached yet
  const { apiData: phasesData } = useFetch(
    isOpen && !phases ? `projects/${project.id}/phases` : null,
  );

  useEffect(() => {
    if (phasesData) onPhasesLoaded(project.id, phasesData.data || []);
  }, [phasesData]);

  const fullProject = detail || project;

  // withStatistics delivers the counters with the list, the phases replace them on expand
  const stats = projectCapacityStats(project, phases);

  const clientName = !project.clientType
    ? t('internal')
    : project.clientType === 'company'
      ? project.client?.name
      : `${project.client?.lastName || ''} ${project.client?.firstName || ''}`;

  // a page that brings its own counters would otherwise get Open and Overdue
  // twice as soon as the phases arrive, once over all users and once its own
  const headerStats = showProjectStats && stats
    ? [
        { key: 'open', label: t('Open'), value: stats.open },
        {
          key: 'overdue',
          label: t('Overdue'),
          value: stats.overdue,
          color: stats.overdue ? colors.error : null,
        },
        ...(showHours
          ? [
              {
                key: 'hours',
                label: t('Hours'),
                value: `${renderHours(stats.workedSeconds / 60)}h`,
              },
            ]
          : []),
        ...extraStats,
      ]
    : extraStats;

  const preview = previewTasks?.length ? (
    <TaskCardList>
      {previewTasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          workPackage={task.workPackage}
          projectId={project.id}
          // the card assigns members itself, the dialog behind onEdit does the rest
          editmode={Boolean(fullProject.editable)}
          isProcessing={processingTaskIds.includes(task.id)}
          // a nested task carries no project, so the card supplies it
          onToggleComplete={
            onTaskToggle && ((item) => onTaskToggle(item, project.id))
          }
          onEdit={onTaskEdit && ((item) => onTaskEdit(item, project.id))}
          onDelete={onTaskDelete && ((item) => onTaskDelete(item, project.id))}
          onUsersChange={
            onTaskUsersChange && ((item) => onTaskUsersChange(item, project.id))
          }
          onFilesChange={
            onTaskFilesChange &&
            ((item, files) => onTaskFilesChange(item, files, project.id))
          }
          onLinkAdd={
            onTaskLinkAdd &&
            ((item, link) => onTaskLinkAdd(item, link, project.id))
          }
          onLinkDelete={
            onTaskLinkDelete &&
            ((item, resource) => onTaskLinkDelete(item, resource, project.id))
          }
          onCommentSubmit={
            onTaskCommentSubmit &&
            ((item, text) => onTaskCommentSubmit(item, text, project.id))
          }
        />
      ))}
    </TaskCardList>
    // without tasks the project stays a single row
  ) : null;

  return (
    <CapacityProjectCard
      project={project}
      isOpen={isOpen}
      onToggle={onToggle}
      stats={headerStats}
      preview={preview}
      teamMembers={projectTeamMembers(fullProject)}
      subtitle={
        stats?.workPackageCount || stats?.phaseCount
          ? `${stats.phaseCount} ${t('Phases')} · ${stats.workPackageCount} ${t(
              'Work Packages',
            )}`
          : clientName
      }
    >
      {!phases ? (
        <ExoSkeleton n={2} size="70px" />
      ) : visiblePhases(phases, taskFilter).length ? (
        visiblePhases(phases, taskFilter).map((phase, index) => (
          <ProjectTaskPhase
            key={phase.id}
            project={fullProject}
            phase={phase}
            filter={taskFilter}
            onLoadingChange={onLoadingChange}
            onStatsChange={(workPackageId, delta) =>
              onStatsChange(project.id, workPackageId, delta)
            }
            defaultExpanded={index === 0}
            // the phases sit on the glass of the project card
            variant="card"
            allowCreate={allowCreate}
          />
        ))
      ) : (
        <Typography fontSize="13px" className="opacity-50 py-2 px-2">
          {phases.length
            ? t('All phases are hidden by the filters')
            : t('No data available')}
        </Typography>
      )}
    </CapacityProjectCard>
  );
};

export default CapacityProject;
